import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ContactlessPayment as Nfc, Camera, QrCode, Phone, FileText, Warning, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BrandLogo } from "../components/brand/BrandLogo";
import { TagStatus } from "../components/common";
import { useApi, errMsg } from "../lib/api";
import { hasWebNfc, startScan } from "../lib/nfc";
import { GROUP_TYPES } from "../lib/constants";
import { useCamera, beep, photoUrl, hhmm, downloadCsv, usePersisted } from "../lib/scan";

const MODES = [["emergency", "Emergency / sick bay"], ["checkin", "Check-in & attendance"], ["verify", "Verify a tag"]];
const HINTS = { emergency: "The person’s critical medical info appears here.", checkin: "Each scan records arrival and can notify parents or HR.", verify: "Check that a tag is active and linked to the right person." };
const HOW = { reader_nfc: "NFC", reader_qr: "QR", camera: "Camera", reader_manual: "Manual" };
const ROLE = { admin: "Group admin", super: "bsafe admin", nurse: "Medical staff", front_desk: "Front desk", viewer: "Viewer" };
const txt = (v) => (Array.isArray(v) ? v.join(", ") : v);
const today = () => new Date().toISOString().slice(0, 10);

const Field = ({ label, value, danger, testid }) => value ? (
  <div className={`rounded-xl p-3 ${danger ? "bg-red-500/15 border border-red-500/40" : "bg-slate-900/60"}`} data-testid={testid}>
    <div className={`overline ${danger ? "text-red-300" : ""}`}>{label}</div><div className="mt-1 font-semibold">{value}</div></div>) : null;

function Medical({ c }) {
  if (!c.medical_shown) return <div className="text-sm text-slate-400" data-testid="reader-medical-off">Medical info is turned off for this group’s reader stations.</div>;
  const any = c.blood_type || c.allergies || c.conditions || c.medication || c.kin;
  if (!any) return <div className="text-sm text-slate-400" data-testid="reader-medical-none">No medical info is shown to responders for this person.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="reader-medical">
      <Field label="Blood" value={c.blood_type} testid="reader-blood" />
      <Field label="Allergies · do not give" value={txt(c.allergies)} danger testid="reader-allergies" />
      <Field label="Conditions" value={txt(c.conditions)} testid="reader-conditions" />
      <Field label="Medication" value={txt(c.medication)} testid="reader-medication" />
      {c.kin && <Field label="Next of kin" value={`${c.kin.name || "Contact"} · ${c.kin.phone}`} testid="reader-kin" />}
    </div>
  );
}

function Actions({ r, group, onIncident }) {
  const c = r.card;
  const full = group.my_role === "nurse" && c.nurse_ok;
  return (<>
    <div className="flex flex-wrap gap-2">
      {c.kin && <a href={`tel:${c.kin.phone}`} data-testid="reader-call-kin" className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 px-4 py-2 text-sm font-bold transition-colors"><Phone size={16} />Call next of kin</a>}
      {full ? <Link to={`/app/g/${group.id}/nurse`} data-testid="reader-open-record" className="inline-flex items-center gap-2 rounded-full border border-slate-500 px-4 py-2 text-sm font-semibold hover:border-white"><FileText size={16} />Open full record</Link>
        : <Button variant="outline" disabled data-testid="reader-open-record" className="rounded-full"><FileText size={16} className="mr-2" />Open full record</Button>}
      <Button variant="outline" className="rounded-full" data-testid="reader-log-incident" onClick={onIncident}><Warning size={16} className="mr-2" />Log incident</Button>
    </div>
    <p className="text-xs text-slate-500 mt-2">Full record opens only for medical staff the family has allowed. Every view is audit-logged.</p>
  </>);
}

function ScanCard({ r, mode, group, station, secs, onClear, onIncident }) {
  const c = r.card;
  const sub = GROUP_TYPES[group.type]?.subOne || "Group";
  return (
    <div className="panel p-6 border-2 border-emerald-500/60" data-testid="reader-result">
      <div className="flex flex-wrap justify-between gap-2 text-xs font-bold"><span className="text-emerald-300" data-testid="reader-read-by">✓ READ BY {(HOW[r.source] || "QR").toUpperCase()} · {hhmm(r.ts)}</span>
        {secs != null && <span className="text-slate-400" data-testid="reader-clear-countdown">Clears in {secs} s</span>}</div>
      <div className="flex items-center gap-4 mt-4">
        {c.photo_token ? <img src={photoUrl(c.photo_token)} alt="" className="w-20 h-20 rounded-2xl object-cover" data-testid="reader-photo" />
          : <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center text-3xl font-black">{r.first_name[0]}</div>}
        <div><div className="text-3xl font-black" data-testid="reader-result-name">{r.first_name} {r.last_name}</div>
          <div className="text-sm text-slate-300 mt-1">{c.subgroup || `No ${sub.toLowerCase()}`}{c.age != null && ` · ${c.age} yrs`} · {r.bs_id === "PERSONAL" ? "Personal QR" : <>ID <span className="font-mono">{r.bs_id}</span></>}</div></div>
      </div>
      <div className="mt-5 space-y-4">
        {mode === "emergency" && <><Medical c={c} /><Actions r={r} group={group} onIncident={onIncident} /></>}
        {mode === "checkin" && <div className="rounded-xl bg-slate-900/60 p-4" data-testid="reader-checked">
          <div className="font-bold text-lg flex items-center gap-2"><CheckCircle size={22} className="text-emerald-400" weight="fill" />{r.type === "checkin" ? "Checked in" : "Checked out"}</div>
          <div className="text-sm text-slate-300 mt-1">{station || "This station"} · {hhmm(r.ts)} · {r.today_in} in today</div>
          {c.parent_alerts && <div className="text-xs text-emerald-300 mt-2" data-testid="reader-parent-notified">Parent notified on WhatsApp (MOCKED)</div>}</div>}
        {mode === "verify" && <div className="grid sm:grid-cols-2 gap-3" data-testid="reader-verify">
          <div className="rounded-xl bg-slate-900/60 p-3"><div className="overline">Tag status</div><div className="mt-2"><TagStatus value={c.tag_status} /> <span className="text-sm text-slate-300">belongs to this group</span></div></div>
          <div className="rounded-xl bg-slate-900/60 p-3"><div className="overline">Profile</div><div className="mt-1 font-semibold">{c.completeness >= 100 ? "Complete" : `Missing info · ${c.completeness}% complete`}</div></div></div>}
      </div>
      <Button className="mt-6 w-full h-12 text-base" data-testid="reader-clear-btn" onClick={onClear}>Clear and scan next</Button>
    </div>
  );
}

function IncidentDialog({ open, onOpenChange, r, gid, station }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const save = async () => {
    try { await api.post(`/groups/${gid}/incidents`, { person_id: r.person_id, note, station }); toast.success("Incident logged"); setNote(""); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-[#111a33]" data-testid="incident-dialog">
      <DialogHeader><DialogTitle>Log incident · {r?.first_name}</DialogTitle></DialogHeader>
      <Textarea data-testid="incident-note-input" rows={4} placeholder="What happened and what was done" value={note} onChange={(e) => setNote(e.target.value)} />
      <Button data-testid="incident-save-btn" disabled={!note.trim()} onClick={save}>Save incident</Button>
    </DialogContent></Dialog>
  );
}

function SidePanels({ log, onExport, onCamera, cam, nfcOn, onTest, cfg, setCfg }) {
  return (
    <div className="space-y-4">
      <div className="panel p-5" data-testid="reader-today"><div className="flex justify-between items-center mb-3"><div className="font-bold">Today at this station</div><span className="text-xs text-slate-400">{log.length}</span></div>
        {log.length === 0 ? <div className="text-sm text-slate-500">No scans yet today.</div> : <div className="max-h-64 overflow-y-auto">{log.map((l, i) => (
          <div key={i} className="flex justify-between text-sm py-2 border-b border-slate-800 last:border-0" data-testid={`reader-today-row-${i}`}><span>{l.name} · {l.how}</span><span className="text-slate-400">{hhmm(l.ts)}</span></div>))}</div>}
        <Button variant="outline" size="sm" className="mt-3 w-full" disabled={!log.length} data-testid="reader-export-log" onClick={onExport}><DownloadSimple size={16} className="mr-2" />Export today’s log</Button></div>
      <div className="panel p-5 text-sm space-y-2" data-testid="reader-connect"><div className="font-bold">Connect a reader</div>
        <p className="text-slate-400">Plug a USB or Bluetooth QR scanner into this computer. It works like a keyboard, so no setup is needed.</p>
        <p className="text-slate-400">On Chrome for Android, tap “Start NFC” and hold a card or wristband to the back of the phone. {nfcOn ? "NFC is listening." : ""}</p>
        <p className="text-slate-400">Keep this page open. Scans appear here straight away.</p>
        <div className="flex flex-wrap gap-2 pt-1"><Button size="sm" variant="outline" data-testid="reader-test-btn" onClick={onTest}>Test my readers</Button>
          <Button size="sm" variant="outline" data-testid="reader-camera-btn" onClick={onCamera}><Camera size={16} className="mr-2" />{cam ? "Stop camera" : "No reader? Use this device’s camera"}</Button></div></div>
      <div className="panel p-5 text-sm space-y-3" data-testid="reader-settings"><div className="font-bold">Station settings</div>
        <label className="flex justify-between items-center">Beep on each scan<Switch data-testid="reader-beep-switch" checked={cfg.beep} onCheckedChange={(v) => setCfg({ ...cfg, beep: v })} /></label>
        <label className="flex justify-between items-center">Auto-clear after 30 seconds<Switch data-testid="reader-autoclear-switch" checked={cfg.autoClear} onCheckedChange={(v) => setCfg({ ...cfg, autoClear: v })} /></label>
        <label className="block space-y-1"><span className="overline">Station name</span><Input data-testid="reader-station-name" placeholder="e.g. Main gate" value={cfg.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} /></label></div>
    </div>
  );
}

function useStation(gid, api, modeRef, dirRef, cfgRef, onResult) {
  const recent = useRef({});
  const submit = useRef(null);
  submit.current = async (code, source) => {
    code = (code || "").trim();
    const mode = modeRef.current;
    const key = `${code}|${mode}|${dirRef.current}`;
    if (!code || (recent.current[key] && Date.now() - recent.current[key] < 5000)) return;
    recent.current[key] = Date.now();
    try {
      const r = await api.post(`/groups/${gid}/attendance`, { code, type: mode === "checkin" ? dirRef.current : "lookup", source, station: cfgRef.current.name });
      if (cfgRef.current.beep) beep();
      onResult({ ok: true, ...r.data, source, mode, ts: new Date().toISOString() });
    } catch (e) { onResult({ ok: false, error: errMsg(e) }); }
  };
  return useRef((c, s) => submit.current(c, s)).current;
}

export default function ReaderStation() {
  const { gid } = useParams();
  const api = useApi();
  const [group, setGroup] = useState(null);
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("emergency");
  const [dir, setDir] = useState("checkin");
  const [cfg, setCfg] = usePersisted(`bsafe_station_${gid}`, { beep: true, autoClear: true, name: "" });
  const [log, setLog] = usePersisted(`bsafe_station_log_${gid}_${today()}`, []);
  const [last, setLast] = useState(null);
  const [secs, setSecs] = useState(null);
  const [nfcOn, setNfcOn] = useState(false);
  const [cam, setCam] = useState(false);
  const [qrSeen, setQrSeen] = useState(false);
  const [code, setCode] = useState("");
  const [incident, setIncident] = useState(false);
  const inputRef = useRef(null);
  const modeRef = useRef(mode); modeRef.current = mode;
  const dirRef = useRef(dir); dirRef.current = dir;
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const logRef = useRef(log); logRef.current = log;
  useEffect(() => { api.get(`/groups/${gid}`).then((r) => setGroup(r.data)); api.get("/me").then((r) => setMe(r.data)).catch(() => {}); }, [api, gid]);

  const onResult = useCallback((r) => {
    setLast(r);
    if (r.ok) {
      setLog([{ name: `${r.first_name} ${r.last_name}`.trim(), how: HOW[r.source] || r.source, ts: r.ts, type: r.mode === "checkin" ? r.type : r.mode }, ...logRef.current].slice(0, 500));
      setSecs(cfgRef.current.autoClear ? 30 : null);
    }
  }, [setLog]);
  const onCode = useStation(gid, api, modeRef, dirRef, cfgRef, onResult);
  useCamera(cam, onCode, "camera");

  useEffect(() => {
    if (secs == null) return undefined;
    if (secs <= 0) { setLast(null); setSecs(null); return undefined; }
    const t = setTimeout(() => setSecs(secs - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);

  const ctrl = useRef(null);
  const toggleNfc = async () => {
    if (nfcOn) { ctrl.current?.abort(); setNfcOn(false); return; }
    try { ctrl.current = new AbortController(); await startScan((url) => onCode(url, "reader_nfc"), ctrl.current.signal); setNfcOn(true); }
    catch (e) { setLast({ ok: false, error: `NFC: ${e.message}` }); }
  };
  useEffect(() => () => ctrl.current?.abort(), []);
  const clear = () => { setLast(null); setSecs(null); inputRef.current?.focus(); };
  const submitCode = (e) => { e.preventDefault(); setQrSeen(true); onCode(code, "reader_qr"); setCode(""); };
  const exportLog = () => downloadCsv(`${cfg.name || "station"}-${today()}.csv`, [["Name", "How", "Time", "Type"], ...log.map((l) => [l.name, l.how, hhmm(l.ts), l.type])]);
  const test = () => { inputRef.current?.focus(); if (hasWebNfc() && !nfcOn) toggleNfc(); toast.info("Scan any bsafe card or wristband now"); };

  if (!group) return <div className="min-h-screen grain p-8 text-slate-400">Loading…</div>;
  return (
    <div className="min-h-screen grain p-4 sm:p-8" data-testid="reader-station">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4"><BrandLogo size={30} tone="dark" />
            <Link to={`/app/g/${gid}`} data-testid="reader-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} />{group.name}</Link></div>
          <div className="text-right text-sm" data-testid="reader-signed-in"><div className="text-slate-400 text-xs">Signed in as</div><div className="font-semibold">{me?.name || me?.email} · {ROLE[group.my_role] || group.my_role}</div></div>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div><h1 className="text-3xl sm:text-4xl font-black">Reader station</h1>
            <p className="text-slate-400 text-sm mt-1">Plug in a QR scanner or NFC reader, then scan any bsafe card or wristband.</p></div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span data-testid="reader-qr-status" className={`rounded-full px-3 py-1.5 flex items-center gap-1.5 ${qrSeen ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}><QrCode size={14} />{qrSeen ? "QR scanner connected" : "QR scanner: waiting"}</span>
            <span data-testid="reader-nfc-status" className={`rounded-full px-3 py-1.5 flex items-center gap-1.5 ${nfcOn ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}><Nfc size={14} />{nfcOn ? "NFC reader connected" : "NFC reader: off"}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="reader-modes">{MODES.map(([k, l]) => (
          <button key={k} data-testid={`reader-mode-${k}`} aria-pressed={mode === k} onClick={() => { setMode(k); clear(); }}
            className={`h-10 px-4 rounded-full text-sm font-semibold transition-colors ${mode === k ? "bg-white text-[#0E1B33]" : "border border-[#2A3F66] text-slate-200 hover:border-slate-400"}`}>{l}</button>))}
          {mode === "checkin" && [["checkin", "Time IN"], ["checkout", "Time OUT"]].map(([k, l]) => (
            <button key={k} data-testid={`reader-dir-${k}`} onClick={() => setDir(k)} className={`h-10 px-4 rounded-full text-sm font-bold transition-colors ${dir === k ? (k === "checkin" ? "bg-emerald-600" : "bg-red-600") : "bg-slate-800 text-slate-300"}`}>{l}</button>))}
        </div>
        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          <div className="space-y-4">
            {last?.ok ? <ScanCard r={last} mode={last.mode} group={group} station={cfg.name} secs={secs} onClear={clear} onIncident={() => setIncident(true)} />
              : <div className={`panel p-10 text-center border-2 border-dashed ${last ? "border-red-500/60" : "border-slate-700"}`} data-testid="reader-waiting">
                <Nfc size={44} className="mx-auto text-slate-500" /><div className="text-2xl font-black mt-3">Ready to scan</div>
                <p className="text-slate-400 mt-2">Hold a card or wristband over the NFC reader, or point the QR scanner at its code.</p>
                <p className="text-slate-500 text-sm mt-1" data-testid="reader-mode-hint">{HINTS[mode]}</p>
                {last && <div className="text-red-300 mt-4 font-semibold" data-testid="reader-result-error">{last.error}</div>}</div>}
            <form onSubmit={submitCode} className="flex gap-2">
              <Input ref={inputRef} autoFocus data-testid="reader-manual-input" placeholder="QR scanner types here · or type a tag ID (BS-…)" value={code} onChange={(e) => setCode(e.target.value)} />
              <Button type="submit" data-testid="reader-manual-submit">Submit</Button>
              <Button type="button" variant="outline" data-testid="reader-nfc-btn" disabled={!hasWebNfc()} onClick={toggleNfc} title={hasWebNfc() ? "" : "Web NFC needs Chrome on Android"}><Nfc size={18} className="mr-1" />{nfcOn ? "Stop NFC" : "Start NFC"}</Button>
            </form>
            <div id="qr-reader" className={`max-w-sm rounded-xl overflow-hidden ${cam ? "" : "hidden"}`} />
          </div>
          <SidePanels log={log} onExport={exportLog} onCamera={() => setCam(!cam)} cam={cam} nfcOn={nfcOn} onTest={test} cfg={cfg} setCfg={setCfg} />
        </div>
      </div>
      <IncidentDialog open={incident} onOpenChange={setIncident} r={last} gid={gid} station={cfg.name} />
    </div>
  );
}
